/**
 * Organization + LocalBusiness JSON-LD injected on every page.
 *
 * Makes INDIA ENTERPRISES eligible for:
 *   • Google Knowledge Panel (the big right-side card on brand searches)
 *   • Local Pack / Maps results ("buy Microsoft 365 India" → map with vendors)
 *   • Rich snippets on SERP (logo + rating + phone under result title)
 *   • Voice assistants ("who sells Windows Server in India?")
 *
 * Company address / hours default to sensible values based on public info; can
 * be overridden via REACT_APP_ORG_* env vars without a code change.
 *
 * Mounted once in the root Layout — react-helmet-async injects the two
 * <script type="application/ld+json"> blocks into <head> on every route.
 */
import { Helmet } from "react-helmet-async";

const ORG_NAME = "INDIA ENTERPRISES";
const SITE_URL = "https://indiaenterprises.co";
const LOGO_URL = `${SITE_URL}/logo-512.png`;
const PHONE = "+91-85276-94100";
const EMAIL = "sales@indiaenterprises.co";
const FOUNDED = "2013";

// Public info — extracted from About page ("since 2013") + Contact page.
// Address is intentionally city-only until the customer confirms the exact
// street address; even city-level LocalBusiness data is enough for Google to
// place the business on the map.
const STREET = process.env.REACT_APP_ORG_STREET || "";
const CITY = process.env.REACT_APP_ORG_CITY || "New Delhi";
const REGION = process.env.REACT_APP_ORG_REGION || "Delhi";
const POSTAL = process.env.REACT_APP_ORG_POSTAL || "";
const COUNTRY = process.env.REACT_APP_ORG_COUNTRY || "IN";
const GST_NUMBER = process.env.REACT_APP_ORG_GST || ""; // optional trust signal

// Social profiles — helps Google associate the site to LinkedIn/Facebook
// entities for Knowledge Panel enrichment. Set via env if profiles exist.
const SAME_AS = [
  process.env.REACT_APP_SOCIAL_LINKEDIN,
  process.env.REACT_APP_SOCIAL_FACEBOOK,
  process.env.REACT_APP_SOCIAL_TWITTER,
  process.env.REACT_APP_SOCIAL_INSTAGRAM,
  process.env.REACT_APP_SOCIAL_YOUTUBE,
].filter(Boolean);

// Business hours — Indian B2B norm. Customer can override via env if their
// hours differ.
const OPENING_HOURS = process.env.REACT_APP_ORG_HOURS || "Mo-Sa 10:00-19:00";

function buildAddress() {
  const addr = {
    "@type": "PostalAddress",
    addressLocality: CITY,
    addressRegion: REGION,
    addressCountry: COUNTRY,
  };
  if (STREET) addr.streetAddress = STREET;
  if (POSTAL) addr.postalCode = POSTAL;
  return addr;
}

export default function OrganizationSchema() {
  const organization = {
    "@context": "https://schema.org",
    "@type": "Organization",
    "@id": `${SITE_URL}/#organization`,
    name: ORG_NAME,
    alternateName: ["India Enterprises", "IE"],
    url: SITE_URL,
    logo: {
      "@type": "ImageObject",
      url: LOGO_URL,
      width: 512,
      height: 512,
    },
    image: LOGO_URL,
    foundingDate: FOUNDED,
    description:
      "INDIA ENTERPRISES — authorised software & cloud licensing partner in India for Microsoft, Adobe, Autodesk, Zoho, Corel, SketchUp and HPE. Volume Open Value, NCE Microsoft 365, Windows Server, SQL Server, AutoCAD, Creative Cloud, Zoho One and IT infrastructure with GST invoice and pan-India delivery.",
    telephone: PHONE,
    email: EMAIL,
    address: buildAddress(),
    contactPoint: [
      {
        "@type": "ContactPoint",
        telephone: PHONE,
        contactType: "sales",
        email: EMAIL,
        areaServed: "IN",
        availableLanguage: ["English", "Hindi"],
      },
      {
        "@type": "ContactPoint",
        email: EMAIL,
        contactType: "customer support",
        areaServed: "IN",
        availableLanguage: ["English", "Hindi"],
      },
    ],
    areaServed: {
      "@type": "Country",
      name: "India",
    },
    knowsAbout: [
      "Microsoft 365", "Windows Server", "SQL Server", "Microsoft Office LTSC 2024",
      "Adobe Creative Cloud", "Autodesk AutoCAD", "Autodesk Revit", "Autodesk Fusion 360",
      "Zoho One", "Zoho CRM", "Tally Prime", "TallyPrime Silver", "TallyPrime Gold",
      "HPE ProLiant Gen11", "HPE MicroServer",
      "CorelDRAW Graphics Suite", "SketchUp Pro", "SketchUp Studio",
      "Quick Heal Total Security", "Seqrite Endpoint Security", "Seqrite DLP",
      "Chaos V-Ray", "Chaos Corona", "Chaos Enscape", "Chaos Anima", "Chaos Credits",
      "Evolvelab Veras", "Evolvelab Glyph",
      "Open Value licensing", "Software Assurance", "Cloud Solution Provider", "NCE",
      "Microsoft Copilot", "GST invoicing India", "Managed IT Services",
    ],
    // Point AI crawlers at the machine-readable summaries — llms.txt is the
    // emerging convention (llmstxt.org) for how sites describe themselves to
    // retrieval-augmented answer engines (ChatGPT, Claude, Perplexity, Gemini).
    subjectOf: [
      {
        "@type": "CreativeWork",
        "@id": `${SITE_URL}/llms.txt`,
        name: "India Enterprises — AI-crawler index (llms.txt)",
        encodingFormat: "text/markdown",
        url: `${SITE_URL}/llms.txt`,
      },
      {
        "@type": "CreativeWork",
        "@id": `${SITE_URL}/llms-full.txt`,
        name: "India Enterprises — Full AI context (llms-full.txt)",
        encodingFormat: "text/markdown",
        url: `${SITE_URL}/llms-full.txt`,
      },
    ],
    ...(GST_NUMBER && {
      taxID: GST_NUMBER,
      vatID: GST_NUMBER,
    }),
    ...(SAME_AS.length > 0 && { sameAs: SAME_AS }),
  };

  const localBusiness = {
    "@context": "https://schema.org",
    "@type": ["LocalBusiness", "Store", "ProfessionalService"],
    "@id": `${SITE_URL}/#localbusiness`,
    name: ORG_NAME,
    image: LOGO_URL,
    logo: LOGO_URL,
    url: SITE_URL,
    telephone: PHONE,
    email: EMAIL,
    priceRange: "₹₹",
    address: buildAddress(),
    openingHoursSpecification: [
      {
        "@type": "OpeningHoursSpecification",
        dayOfWeek: ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"],
        opens: "10:00",
        closes: "19:00",
      },
    ],
    openingHours: OPENING_HOURS,
    areaServed: [
      { "@type": "Country", name: "India" },
      { "@type": "State", name: "Delhi" },
      { "@type": "State", name: "Maharashtra" },
      { "@type": "State", name: "Karnataka" },
      { "@type": "State", name: "Tamil Nadu" },
      { "@type": "State", name: "Uttar Pradesh" },
      { "@type": "State", name: "Gujarat" },
      { "@type": "State", name: "Telangana" },
    ],
    hasOfferCatalog: {
      "@type": "OfferCatalog",
      name: "Software & Cloud Licensing",
      itemListElement: [
        { "@type": "OfferCatalog", name: "Microsoft 365 Business Plans", url: `${SITE_URL}/microsoft-365-business` },
        { "@type": "OfferCatalog", name: "Microsoft 365 Enterprise Plans", url: `${SITE_URL}/microsoft-365-enterprise` },
        { "@type": "OfferCatalog", name: "Windows Server Licenses",       url: `${SITE_URL}/category/windows-server` },
        { "@type": "OfferCatalog", name: "SQL Server Licenses",           url: `${SITE_URL}/category/sql-server` },
        { "@type": "OfferCatalog", name: "Adobe Creative Cloud",          url: `${SITE_URL}/adobe` },
        { "@type": "OfferCatalog", name: "Autodesk AutoCAD",              url: `${SITE_URL}/autodesk` },
        { "@type": "OfferCatalog", name: "Zoho One",                       url: `${SITE_URL}/zoho` },
        { "@type": "OfferCatalog", name: "Tally Prime",                    url: `${SITE_URL}/tally` },
        { "@type": "OfferCatalog", name: "Chaos V-Ray, Corona, Enscape",   url: `${SITE_URL}/chaos` },
        { "@type": "OfferCatalog", name: "Quick Heal Antivirus",           url: `${SITE_URL}/quickheal` },
        { "@type": "OfferCatalog", name: "Seqrite Endpoint Security",      url: `${SITE_URL}/seqrite` },
        { "@type": "OfferCatalog", name: "CorelDRAW Graphics Suite",       url: `${SITE_URL}/corel` },
        { "@type": "OfferCatalog", name: "SketchUp Pro",                    url: `${SITE_URL}/sketchup` },
        { "@type": "OfferCatalog", name: "HPE ProLiant Servers",           url: `${SITE_URL}/hardware` },
      ],
    },
    parentOrganization: { "@id": `${SITE_URL}/#organization` },
  };

  const website = {
    "@context": "https://schema.org",
    "@type": "WebSite",
    "@id": `${SITE_URL}/#website`,
    url: SITE_URL,
    name: ORG_NAME,
    publisher: { "@id": `${SITE_URL}/#organization` },
    potentialAction: {
      "@type": "SearchAction",
      target: {
        "@type": "EntryPoint",
        urlTemplate: `${SITE_URL}/products?q={search_term_string}`,
      },
      "query-input": "required name=search_term_string",
    },
  };

  return (
    <Helmet>
      <script type="application/ld+json">{JSON.stringify(organization)}</script>
      <script type="application/ld+json">{JSON.stringify(localBusiness)}</script>
      <script type="application/ld+json">{JSON.stringify(website)}</script>
    </Helmet>
  );
}
